import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ACOMODACOES,
  ETAPAS_FUNIL,
  MODALIDADES,
  MOTIVOS_ENCERRAMENTO,
  NIVEIS_ENGAJAMENTO,
  NIVEIS_URGENCIA,
  ROTULO_ACOMODACAO,
  ROTULO_ENGAJAMENTO,
  ROTULO_ETAPA,
  ROTULO_MODALIDADE,
  ROTULO_MOTIVO_ENCERRAMENTO,
  ROTULO_TIPO_CONTRATO,
  ROTULO_URGENCIA,
  TETO_DAS_OBSERVACOES,
  TIPOS_CONTRATO,
  type TipoContrato,
  exigeMotivo,
  exigeProximoPasso,
  humanizarPergunta,
  type EtapaFunil,
  type OrigemLead,
} from '@reducee/contracts';
import { useState, type FormEvent } from 'react';

import { chamarApi, ErroDaApi } from '../api/cliente.ts';
import { useSessao } from '../autenticacao/sessao.tsx';
import { PRODUTOS, comValorAtual } from '../dominio/catalogo.ts';
import { useOperadoras } from '../dominio/operadoras.ts';
import { useOrigens } from '../dominio/origens.ts';
import { ConfirmarExclusao } from './ConfirmarExclusao.tsx';
import { CampoSelecao, CampoTexto, CampoTextoLongo } from './Campo.tsx';
import { Dialogo } from './Dialogo.tsx';
import { DocumentosDoContato } from './DocumentosDoContato.tsx';

type Parceiro = { id: string; nome: string; status: string };

/** Uma pergunta do formulário da Meta e o que a pessoa escreveu nela. */
type RespostaDoFormulario = { pergunta: string; respostas: string[] };

/**
 * A identidade do Contato desta venda, buscada sob demanda.
 *
 * Fora da listagem de propósito, pelo mesmo motivo das Respostas do
 * Formulário: telefone, e-mail e CPF são dado pessoal, e a listagem do funil
 * devolve até 500 Oportunidades e se refaz a cada gravação. Aqui eles saem uma
 * vez, quando alguém abriu aquela Oportunidade para trabalhar nela.
 */
type ContatoDaOportunidade = {
  id: string;
  nome: string;
  telefone: string | null;
  whatsapp: string | null;
  email: string | null;
  documento: string | null;
  perfilSocial: string | null;
};

/**
 * O que a Comercial anotou sobre o Lead da venda, buscado sob demanda.
 *
 * Fora da listagem pelo mesmo motivo do contato: o texto costuma ser colado de
 * uma conversa. `temLead` é falso nas vendas da carteira importada, que nascem
 * sem Lead — para elas o campo não aparece, porque não há onde guardar.
 */
type ObservacoesDaVenda = { temLead: boolean; observacoes: string | null };

/** A ficha da venda, como a API a devolve, para a janela abrir preenchida. */
export type OportunidadeParaEditar = {
  id: string;
  produto: string;
  operadora: string | null;
  tipoContrato: string;
  tipoPlano: string | null;
  acomodacao: string;
  modalidade: string;
  planoAnterior: string | null;
  numeroVidas: number | null;
  valorMensalCentavos: number;
  prazoContratacao: string | null;
  urgencia: string;
  engajamento: string;
  canalComissao: string;
  parceiroId: string | null;
  etapa: EtapaFunil;
  proximoPasso: string | null;
  dataProximoPasso: string | null;
  origem: OrigemLead | null;
  campanha: string | null;
  contato: { id: string; nome: string; naoContatar: boolean };
  responsavel: { id: string; nome: string };
  criadaEm: string;
};

/**
 * `contatoId` e `nome` vêm nulos quando o Contato encontrado está fora do
 * alcance de quem perguntou: dá para saber que existe, e não de quem é.
 */
type Duplicidade = { contatoId: string | null; nome: string | null; campos: string[] };

/**
 * Quem é o Contato desta venda: os dados digitados agora, ou um Contato que já
 * existe. A API exige exatamente um dos dois — é ela que recusa criar uma
 * segunda identidade sem decisão explícita, e esta janela só apresenta a
 * escolha.
 */
type IdentidadeDigitada = {
  contato: Record<string, unknown>;
  confirmarNovaIdentidade?: boolean;
};
type Identidade = IdentidadeDigitada | { contatoId: string };

/** Uma gravação: os campos do formulário mais a identidade decidida. */
type Tentativa = {
  campos: FormData;
  identidade: Identidade;
  /**
   * Na edição, assume que a identidade corrigida é mesmo outra pessoa.
   *
   * Corrigir um telefone digitado errado pode encostar o Contato em alguém que
   * já está na base, e aí a API recusa. Isto é a resposta de quem olhou a
   * lista e disse que são duas pessoas diferentes.
   */
  confirmarOutraIdentidade?: boolean;
};

function lerTexto(campos: FormData, nome: string): string | undefined {
  return String(campos.get(nome) ?? '').trim() || undefined;
}

function identidadeDigitada(campos: FormData): IdentidadeDigitada {
  const documento = lerTexto(campos, 'documento');
  return {
    contato: {
      tipo: documento?.replace(/\D/g, '').length === 14 ? 'EMPRESA' : 'PESSOA',
      nome: lerTexto(campos, 'cliente'),
      documento,
      telefone: lerTexto(campos, 'telefone'),
      whatsapp: lerTexto(campos, 'whatsapp'),
      email: lerTexto(campos, 'email'),
      perfilSocial: lerTexto(campos, 'perfilSocial'),
      naoContatar: campos.get('contatoPermitido') === 'nao',
    },
  };
}

function extrairDuplicidades(erro: ErroDaApi): Duplicidade[] {
  const lista = erro.corpo?.['duplicidades'];
  return Array.isArray(lista) ? (lista as Duplicidade[]) : [];
}

const DICA_DAS_OBSERVACOES = `Cole aqui o que souber do lead: conversa, anotações de ligação. Vale para todas as vendas dele. Até ${TETO_DAS_OBSERVACOES.toLocaleString('pt-BR')} caracteres.`;

const CONSENTIMENTOS = [
  'Meta Lead Ads',
  'Formulário/site',
  'WhatsApp iniciado pelo lead',
  'Indicação',
  'Contato comercial legítimo',
  'Outro',
];

/**
 * Props que fazem um campo mostrar o que existe e recusar digitação.
 *
 * `readOnly` e não `disabled`: o telefone e o e-mail estão ali para a Comercial
 * copiar antes de ligar, e campo desabilitado não deixa nem selecionar o texto.
 *
 * O placeholder troca junto. O da edição não é um exemplo do que digitar — é a
 * resposta de que aquele dado não existe no cadastro.
 */
function apenasLeitura(
  ativo: boolean,
  valor: string | null | undefined,
  carregando = false,
): { value?: string; readOnly?: boolean; placeholder?: string } {
  if (!ativo) return {};
  // Enquanto a busca está em voo não se sabe se o dado existe, e dizer "não
  // informado" ali seria esconder o que existe — o defeito que esta janela
  // acabou de deixar de ter.
  return {
    value: valor ?? '',
    readOnly: true,
    placeholder: carregando ? 'Carregando…' : 'Não informado',
  };
}

/**
 * Cadastro de uma venda inteira numa janela só.
 *
 * O protótipo pede identidade, entrada comercial e tentativa de venda no mesmo
 * formulário, e é assim que a operação trabalha. Por baixo, porém, continuam
 * sendo três passos do domínio: o Contato nasce com o Lead, a Oportunidade
 * nasce sobre ele, e só então a etapa do funil é aplicada — porque avançar no
 * funil tem regras que a criação não tem.
 */
export function NovaOportunidade({
  oportunidade,
  aoFechar,
}: {
  /** Quando vem, a janela edita em vez de criar. */
  oportunidade?: OportunidadeParaEditar;
  aoFechar: () => void;
}): React.JSX.Element {
  const editando = oportunidade !== undefined;
  const { usuaria } = useSessao();
  const operadoras = useOperadoras();
  const origens = useOrigens();
  const ehAdministradora = usuaria?.papel === 'ADMINISTRADORA';
  const clienteDeConsultas = useQueryClient();
  const [erro, setErro] = useState<string | null>(null);
  const [duplicidades, setDuplicidades] = useState<Duplicidade[] | null>(null);
  /** O que foi preenchido, guardado para regravar depois da escolha. */
  const [pendente, setPendente] = useState<FormData | null>(null);
  const [etapa, setEtapa] = useState<EtapaFunil>(oportunidade?.etapa ?? 'LEAD');
  /** Excluir abre uma confirmação que mostra o que sai junto (NEX-223). */
  const [excluindo, setExcluindo] = useState(false);
  const [origem, setOrigem] = useState<OrigemLead>(oportunidade?.origem ?? 'REDUCEE');

  // Só quem está ativo pode receber uma venda nova; parceiro inativo continua
  // nos registros antigos, mas não entra em venda nova.
  const parceiros = useQuery({
    queryKey: ['configuracoes'],
    queryFn: () => chamarApi<{ parceiros: Parceiro[] }>('/configuracoes'),
    enabled: origem === 'PARCEIRO',
  });
  /**
   * No protótipo, trocar o Responsável nesta janela é a transferência. Só faz
   * sentido ao editar: um lead novo nasce sempre com a Administradora.
   */
  const usuarias = useQuery({
    queryKey: ['usuarias'],
    queryFn: () => chamarApi<Array<{ id: string; nome: string; ativa: boolean }>>('/usuarias'),
    // `/usuarias` é exclusiva da Administradora. Pedir de outro papel só
    // rendia um 403, e a lista vazia que sobrava deixava o campo em branco —
    // como se a venda não tivesse dono.
    enabled: editando && ehAdministradora,
  });
  const responsaveis = (usuarias.data ?? []).filter((uma) => uma.ativa);

  /**
   * O que a pessoa respondeu no formulário de origem, para a Comercial ler.
   *
   * Só na edição: em cadastro manual ainda não existe Oportunidade, e portanto
   * não há formulário de origem para buscar.
   */
  const idEmEdicao = oportunidade?.id;
  const respostasDoFormulario = useQuery({
    queryKey: ['oportunidades', idEmEdicao, 'respostas-formulario'],
    queryFn: () =>
      chamarApi<{ respostas: RespostaDoFormulario[] }>(
        `/oportunidades/${idEmEdicao}/respostas-formulario`,
      ),
    enabled: idEmEdicao !== undefined,
  });
  const respostas = respostasDoFormulario.data?.respostas ?? [];

  /**
   * Quem é a pessoa desta venda, para a janela abrir mostrando o que já existe.
   *
   * Só na edição, e por rota própria: em cadastro manual o Contato ainda está
   * sendo digitado, e não há o que buscar.
   */
  const contatoDaOportunidade = useQuery({
    queryKey: ['oportunidades', idEmEdicao, 'contato'],
    queryFn: () => chamarApi<ContatoDaOportunidade>(`/oportunidades/${idEmEdicao}/contato`),
    enabled: idEmEdicao !== undefined,
  });
  const contato = contatoDaOportunidade.data;

  /**
   * O que já foi anotado sobre o Lead desta venda.
   *
   * Só na edição, e por rota própria, como o contato. Em cadastro não há
   * Oportunidade, e portanto nada a buscar — o campo nasce vazio.
   */
  const observacoesDaVenda = useQuery({
    queryKey: ['oportunidades', idEmEdicao, 'observacoes'],
    queryFn: () => chamarApi<ObservacoesDaVenda>(`/oportunidades/${idEmEdicao}/observacoes`),
    enabled: idEmEdicao !== undefined,
  });

  const parceirosAtivos = (parceiros.data?.parceiros ?? []).filter(
    (parceiro) => parceiro.status === 'ATIVO',
  );

  const salvar = useMutation({
    mutationFn: async ({ campos, identidade, confirmarOutraIdentidade }: Tentativa) => {
      const texto = (nome: string): string | undefined => lerTexto(campos, nome);

      // Vidas em branco significa "não informado", e isso é diferente de 1: o
      // total de vidas do Dashboard e da Carteira soma este número. Omitido do
      // corpo, ele chega ao banco como nulo em vez de virar um palpite somado.
      // `Number('')` daria 0, que o DTO recusa por não ser positivo.
      const vidasDigitadas = texto('vidas');
      const numeroVidas = vidasDigitadas === undefined ? undefined : Number(vidasDigitadas);

      if (oportunidade) {
        // A identidade primeiro, e por porta própria: ela mora no Contato, não
        // na Oportunidade. Vindo antes, uma colisão de telefone interrompe o
        // salvamento inteiro em vez de deixar a venda gravada e a correção não.
        await chamarApi(`/contatos/${oportunidade.contato.id}`, {
          metodo: 'PATCH',
          corpo: { ...identidadeDigitada(campos).contato, confirmarOutraIdentidade },
        });

        await chamarApi(`/oportunidades/${oportunidade.id}`, {
          metodo: 'PATCH',
          corpo: {
            produto: texto('produto'),
            operadora: texto('operadora'),
            tipoContrato: texto('tipoContrato'),
            tipoPlano: texto('tipoPlano'),
            acomodacao: texto('acomodacao') ?? 'NAO_SE_APLICA',
            modalidade: texto('modalidade') ?? 'NAO_SE_APLICA',
            planoAnterior: texto('planoAnterior'),
            numeroVidas,
            valorMensal: String(campos.get('valor') ?? ''),
            urgencia: texto('urgencia') ?? 'ATE_30_DIAS',
            engajamento: texto('engajamento') ?? 'MEDIO',
            // Não estão no formulário do protótipo: seguem como nasceram.
            canalComissao: oportunidade.canalComissao,
            parceiroId: texto('parceiroId') ?? oportunidade.parceiroId ?? undefined,
            prazoContratacao: oportunidade.prazoContratacao?.slice(0, 10),
            // Só quando o campo existe no formulário, isto é, quando a janela
            // leu o que estava guardado. Vazio apaga, e um campo que abriu
            // vazio por falha de leitura apagaria a anotação de outra pessoa.
            observacoes: campos.has('observacoes') ? String(campos.get('observacoes')) : undefined,
          },
        });

        // Transferir é atômico do lado da API: move Lead, cadências e tarefas.
        const destino = texto('responsavelId');
        if (destino && destino !== oportunidade.responsavel.id) {
          await chamarApi(`/oportunidades/${oportunidade.id}/transferir`, {
            metodo: 'POST',
            corpo: { responsavelId: destino },
          });
        }

        // A etapa tem porta própria porque cobra próximo passo ou motivo.
        //
        // Só quando a etapa muda, e não quando só o próximo passo mudou: essa
        // rota reescreve `fechadaEm` e `perdidaEm` a cada chamada, então
        // chamá-la com a etapa igual mudaria o mês de competência de uma venda
        // já fechada. Corrigir só o próximo passo continua sendo descartado em
        // silêncio — é defeito conhecido, e a saída passa pela API distinguir
        // "não mandei" de "apaguei".
        if (etapa !== oportunidade.etapa) {
          await chamarApi(`/oportunidades/${oportunidade.id}/etapa`, {
            metodo: 'PATCH',
            corpo: {
              etapa,
              proximoPasso: texto('proximoPasso'),
              dataProximoPasso: texto('dataProximoPasso'),
              motivoEncerramento: texto('motivoEncerramento'),
            },
          });
        }
        return;
      }

      // Uma chamada só: a API percorre Contato, Lead, Oportunidade e, quando a
      // venda já entra Fechada, o Contrato e a Implantação — numa transação.
      // Em quatro chamadas emendadas aqui, falhar no meio deixava Contato e
      // Lead órfãos, ou a venda parada em LEAD com Fechado marcado.
      await chamarApi('/oportunidades', {
        metodo: 'POST',
        corpo: {
          ...identidade,
          lead: {
            origem: texto('origem') ?? 'REDUCEE',
            campanha: texto('campanha'),
            consentimento: texto('consentimento'),
            observacoes: texto('observacoes'),
          },
          produto: texto('produto'),
          operadora: texto('operadora'),
          tipoContrato: texto('tipoContrato'),
          tipoPlano: texto('tipoPlano'),
          acomodacao: texto('acomodacao') ?? 'NAO_SE_APLICA',
          modalidade: texto('modalidade') ?? 'NAO_SE_APLICA',
          planoAnterior: texto('planoAnterior'),
          numeroVidas,
          valorMensal: String(campos.get('valor') ?? ''),
          parceiroId: texto('parceiroId'),
          urgencia: texto('urgencia') ?? 'ATE_30_DIAS',
          engajamento: texto('engajamento') ?? 'MEDIO',
          etapa,
          proximoPasso: texto('proximoPasso'),
          dataProximoPasso: texto('dataProximoPasso'),
          motivoEncerramento: texto('motivoEncerramento'),
        },
      });
    },
    onSuccess: async () => {
      await clienteDeConsultas.invalidateQueries({ queryKey: ['oportunidades'] });
      await clienteDeConsultas.invalidateQueries({ queryKey: ['leads'] });
      await clienteDeConsultas.invalidateQueries({ queryKey: ['contatos'] });
      await clienteDeConsultas.invalidateQueries({ queryKey: ['dashboard'] });
      await clienteDeConsultas.invalidateQueries({ queryKey: ['atender-agora'] });
      await clienteDeConsultas.invalidateQueries({ queryKey: ['implantacoes'] });
      aoFechar();
    },
    onError: (causa: unknown) => {
      if (causa instanceof ErroDaApi && causa.status === 409) {
        setDuplicidades(extrairDuplicidades(causa));
        return;
      }
      // Só a recusa das observações sobe com a mensagem própria: é a que a
      // API escreve em português e diz o teto. As outras validações trazem o
      // texto padrão do zod, e "Dados inválidos." é melhor que ele.
      const problemaDasObservacoes =
        causa instanceof ErroDaApi
          ? causa.problemas.find(
              (problema) =>
                problema.campo === 'observacoes' || problema.campo === 'lead.observacoes',
            )
          : undefined;

      setErro(
        problemaDasObservacoes?.mensagem ??
          (causa instanceof ErroDaApi ? causa.message : 'Não foi possível salvar.'),
      );
    },
  });

  function aoEnviar(evento: FormEvent<HTMLFormElement>): void {
    evento.preventDefault();
    const campos = new FormData(evento.currentTarget);
    setErro(null);
    setDuplicidades(null);
    setPendente(campos);
    salvar.mutate({ campos, identidade: identidadeDigitada(campos) });
  }

  /**
   * Reutilizar a identidade é o que dá ao mesmo cliente uma segunda venda: a
   * Oportunidade nova nasce sobre o Contato que já existe, e a jornada dele
   * continua num lugar só.
   */
  function associarAoContato(contatoId: string): void {
    if (!pendente) return;
    setDuplicidades(null);
    salvar.mutate({ campos: pendente, identidade: { contatoId } });
  }

  function registrarComoOutraIdentidade(): void {
    if (!pendente) return;
    setDuplicidades(null);
    salvar.mutate({
      campos: pendente,
      identidade: { ...identidadeDigitada(pendente), confirmarNovaIdentidade: true },
      confirmarOutraIdentidade: true,
    });
  }

  // Na edição a janela espera a identidade e as observações chegarem antes de
  // desenhar. Os campos são não-controlados: desenhar antes gravaria o
  // `defaultValue` vazio por cima do telefone, ou da anotação, que existe, no
  // primeiro salvamento.
  if (editando && (contatoDaOportunidade.isPending || observacoesDaVenda.isPending)) {
    return (
      <Dialogo area="Comercial" titulo="Editar oportunidade / venda" aoFechar={aoFechar}>
        <p className="text-[13px] text-texto-suave">Carregando a ficha…</p>
      </Dialogo>
    );
  }

  return (
    <Dialogo
      area="Comercial"
      titulo={editando ? 'Editar oportunidade / venda' : 'Nova oportunidade / venda'}
      aoFechar={aoFechar}
    >
      <form onSubmit={aoEnviar} data-teste="formulario-oportunidade">
        <div className="grid gap-3 md:grid-cols-2">
          <CampoTexto
            rotulo="Data"
            name="data"
            type="date"
            required
            {...(editando
              ? { value: oportunidade.criadaEm.slice(0, 10), readOnly: true }
              : { defaultValue: new Date().toISOString().slice(0, 10) })}
          />
          {/* A responsável não é escolhida aqui: toda entrada nasce com a
            * Administradora e só muda por direcionamento explícito. */}
          {editando && !ehAdministradora ? (
            /* De quem é a venda, escrito pela aplicação a partir do próprio
             * registro. Sem consulta e sem escolha: transferir é da
             * Administradora, e oferecer o controle aqui só produziria um
             * salvamento pela metade — o PATCH passa, a transferência toma
             * 403 depois. */
            <CampoTexto
              rotulo="Responsável"
              name="responsavelDaVenda"
              value={oportunidade.responsavel.nome}
              readOnly
              dica="Quem responde por esta venda. Transferir é atribuição da Administradora."
            />
          ) : editando ? (
            <CampoSelecao
              rotulo="Responsável"
              name="responsavelId"
              defaultValue={oportunidade.responsavel.id}
              dica="Trocar move junto o Lead, as cadências em andamento e as tarefas abertas."
            >
              {responsaveis.map((usuaria) => (
                <option key={usuaria.id} value={usuaria.id}>
                  {usuaria.nome}
                </option>
              ))}
            </CampoSelecao>
          ) : (
            /* Quem cadastra fica com a entrada, nos dois papéis. O campo
             * mostra o nome de quem está logada em vez de um rótulo genérico:
             * antes dizia "Administradora" mesmo para a Comercial, que
             * cadastrava e via a venda nascer no nome de outra pessoa. */
            <CampoTexto
              rotulo="Responsável"
              name="responsavelDeEntrada"
              value={usuaria?.nome ?? ''}
              readOnly
              dica="A venda nasce no seu nome. Transferir depois é atribuição da Administradora."
            />
          )}

          {/*
            Na edição a identidade abre preenchida e aceita correção: ela viaja
            por porta própria, o `PATCH /contatos/:id`. O WhatsApp é o campo que
            mais depende disso — a Meta entrega o telefone, nunca a confirmação
            de que aquele número tem WhatsApp, então ele só existe se alguém
            digitar depois de falar com a pessoa.
          */}
          <CampoTexto
            rotulo="Cliente / empresa"
            name="cliente"
            required
            className="md:col-span-2"
            defaultValue={contato?.nome ?? oportunidade?.contato.nome ?? ''}
          />

          <CampoTexto
            rotulo="Telefone"
            name="telefone"
            placeholder="(71) 99999-9999"
            defaultValue={contato?.telefone ?? ''}
          />
          <CampoTexto
            rotulo="WhatsApp"
            name="whatsapp"
            placeholder="5571999999999"
            defaultValue={contato?.whatsapp ?? ''}
          />

          <CampoTexto
            rotulo="E-mail"
            name="email"
            type="email"
            placeholder="lead@email.com"
            defaultValue={contato?.email ?? ''}
          />
          <CampoTexto
            rotulo="Perfil social"
            name="perfilSocial"
            placeholder="LinkedIn / Instagram / URL"
            defaultValue={contato?.perfilSocial ?? ''}
          />

          <CampoTexto
            rotulo="CPF / CNPJ"
            name="documento"
            placeholder="Somente para identificação e duplicidade"
            defaultValue={contato?.documento ?? ''}
          />
          {/*
            O consentimento nasce com o Lead e não é relido aqui. Na edição ele
            sai da janela em vez de aparecer vazio: campo ausente é melhor que
            campo que responde "não informado" sobre um dado que pode existir.
          */}
          {editando ? null : (
            <CampoSelecao rotulo="Consentimento / origem" name="consentimento" defaultValue="">
              <option value="">Não informado</option>
              {CONSENTIMENTOS.map((opcao) => (
                <option key={opcao} value={opcao}>
                  {opcao}
                </option>
              ))}
            </CampoSelecao>
          )}

          <CampoSelecao
            rotulo="Contato permitido?"
            name="contatoPermitido"
            defaultValue={oportunidade?.contato.naoContatar ? 'nao' : 'sim'}
            disabled={editando}
          >
            <option value="sim">Sim, pode contatar</option>
            <option value="nao">Não contatar</option>
          </CampoSelecao>
          <CampoSelecao
            rotulo="Etapa"
            name="etapa"
            value={etapa}
            onChange={(evento) => setEtapa(evento.target.value as EtapaFunil)}
          >
            {ETAPAS_FUNIL.map((opcao) => (
              <option key={opcao} value={opcao}>
                {ROTULO_ETAPA[opcao]}
              </option>
            ))}
          </CampoSelecao>

          <CampoTexto
            rotulo="Próximo passo"
            name="proximoPasso" defaultValue={oportunidade?.proximoPasso ?? ''}
            required={exigeProximoPasso(etapa)}
            placeholder="Ex.: ligar para decisor, enviar proposta"
          />
          <CampoTexto
            rotulo="Data do próximo passo"
            name="dataProximoPasso" defaultValue={oportunidade?.dataProximoPasso?.slice(0, 10) ?? ''}
            type="date"
            required={exigeProximoPasso(etapa)}
          />

          <CampoSelecao
            rotulo="Motivo de perda / reciclagem"
            name="motivoEncerramento"
            defaultValue=""
            required={exigeMotivo(etapa)}
          >
            <option value="">Não se aplica</option>
            {MOTIVOS_ENCERRAMENTO.map((motivo) => (
              <option key={motivo} value={motivo}>
                {ROTULO_MOTIVO_ENCERRAMENTO[motivo]}
              </option>
            ))}
          </CampoSelecao>
          <CampoSelecao
            rotulo="Origem"
            name="origem"
            value={origem}
            onChange={(evento) => setOrigem(evento.target.value as OrigemLead)}
            disabled={editando}
          >
            {/* As ativas do cadastro: quem decide o que se oferece é a
              * Administradora, em Configurações. Na edição o campo só mostra, e
              * a origem gravada aparece mesmo desativada. */}
            {origens.todas
              .filter((opcao) => opcao.ativa || opcao.codigo === oportunidade?.origem)
              .map((opcao) => (
                <option key={opcao.codigo} value={opcao.codigo}>
                  {opcao.nome}
                </option>
              ))}
          </CampoSelecao>

          {origem === 'PARCEIRO' ? (
            <CampoSelecao
              rotulo="Parceiro"
              name="parceiroId"
              required
              defaultValue=""
              dica={
                parceirosAtivos.length === 0
                  ? 'Nenhum parceiro ativo. Cadastre em Configurações.'
                  : undefined
              }
            >
              <option value="" disabled>
                Selecione
              </option>
              {parceirosAtivos.map((parceiro) => (
                <option key={parceiro.id} value={parceiro.id}>
                  {parceiro.nome}
                </option>
              ))}
            </CampoSelecao>
          ) : null}

          <CampoTexto
            rotulo="Campanha / conjunto"
            name="campanha"
            placeholder="Ex.: META Setembro 2026"
            {...apenasLeitura(editando, oportunidade?.campanha)}
          />
          {/*
            Sem seleção de partida, e não numa operadora qualquer: o valor era
            enviado no salvamento, então bastava alguém abrir a ficha para
            corrigir outra coisa e clicar em Salvar para o chute virar dado.
            Em branco o banco guarda "não informado", que é a verdade.
          */}
          <CampoSelecao
            key={operadoras.pronta ? 'operadoras-prontas' : 'operadoras-carregando'}
            rotulo="Operadora"
            name="operadora"
            defaultValue={oportunidade?.operadora ?? ''}
          >
            <option value="">— selecione —</option>
            {comValorAtual(operadoras.nomes, oportunidade?.operadora).map((operadora) => (
              <option key={operadora} value={operadora}>
                {operadora}
              </option>
            ))}
          </CampoSelecao>

          <CampoSelecao rotulo="Produto" name="produto" defaultValue={oportunidade?.produto ?? 'Saúde'}>
            {PRODUTOS.map((produto) => (
              <option key={produto} value={produto}>
                {produto}
              </option>
            ))}
          </CampoSelecao>
          <CampoSelecao rotulo="Tipo de contrato" name="tipoContrato" required defaultValue={oportunidade?.tipoContrato ?? ''}>
            <option value="" disabled>
              Selecione
            </option>
            {tiposDisponiveis(oportunidade?.tipoContrato).map((tipo) => (
              <option key={tipo} value={tipo}>
                {ROTULO_TIPO_CONTRATO[tipo]}
              </option>
            ))}
          </CampoSelecao>

          <CampoTexto
            rotulo="Tipo de plano"
            name="tipoPlano" defaultValue={oportunidade?.tipoPlano ?? ''}
            placeholder="Ex.: Regional, Nacional, Executivo"
          />
          <CampoSelecao rotulo="Acomodação" name="acomodacao" defaultValue={oportunidade?.acomodacao ?? 'NAO_SE_APLICA'}>
            {ACOMODACOES.map((acomodacao) => (
              <option key={acomodacao} value={acomodacao}>
                {ROTULO_ACOMODACAO[acomodacao]}
              </option>
            ))}
          </CampoSelecao>

          <CampoSelecao rotulo="Modalidade" name="modalidade" defaultValue={oportunidade?.modalidade ?? 'NAO_SE_APLICA'}>
            {MODALIDADES.map((modalidade) => (
              <option key={modalidade} value={modalidade}>
                {ROTULO_MODALIDADE[modalidade]}
              </option>
            ))}
          </CampoSelecao>
          <CampoTexto
            rotulo="Plano anterior"
            name="planoAnterior" defaultValue={oportunidade?.planoAnterior ?? ''}
            placeholder="Operadora / plano anterior"
          />

          <CampoTexto
            rotulo="Vidas"
            name="vidas"
            type="number"
            min={1}
            defaultValue={oportunidade?.numeroVidas ?? ''}
            placeholder="Não informado"
            dica="Em branco grava não informado. Este número é somado no total de vidas do Dashboard e da Carteira."
          />
          <CampoTexto
            rotulo="Valor estimado / mensalidade (R$)"
            name="valor"
            defaultValue={
              oportunidade ? (oportunidade.valorMensalCentavos / 100).toFixed(2) : ''
            }
            type="number"
            step=".01"
            min={0}
            required
            dica="Usado também no score automático do lead."
          />

          {/*
            As respostas ficam encostadas em Vidas e Valor estimado porque é
            delas que a Comercial tira o que digita nesses dois campos — lendo e
            decidindo, nunca por preenchimento automático. Elas chegam em texto
            livre, e derivar valor daqui erraria em silêncio justamente no campo
            que alimenta o score, que ordena a fila Atender Agora.
          */}
          {respostas.length > 0 ? (
            <div
              data-teste="oportunidade-respostas-formulario"
              className="rounded-[9px] border border-linha bg-marinho-suave p-3 md:col-span-2"
            >
              <p className="text-[11px] font-extrabold text-texto-suave">
                O que a pessoa respondeu
              </p>
              <dl className="mt-2 grid gap-2 md:grid-cols-2">
                {respostas.map((resposta) => (
                  <div key={resposta.pergunta} data-teste="resposta-do-formulario">
                    <dt className="text-[11px] text-texto-suave">
                      {humanizarPergunta(resposta.pergunta)}
                    </dt>
                    <dd className="text-[13px] font-[750]">{resposta.respostas.join(', ')}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ) : null}

          {oportunidade ? (
            <DocumentosDoContato contatoId={oportunidade.contato.id} permitirEnvio compacto />
          ) : null}

          <CampoSelecao rotulo="Prazo para contratação" name="urgencia" defaultValue={oportunidade?.urgencia ?? 'ATE_30_DIAS'}>
            {NIVEIS_URGENCIA.map((nivel) => (
              <option key={nivel} value={nivel}>
                {ROTULO_URGENCIA[nivel]}
              </option>
            ))}
          </CampoSelecao>
          <CampoSelecao rotulo="Engajamento" name="engajamento" defaultValue={oportunidade?.engajamento ?? 'MEDIO'}>
            {NIVEIS_ENGAJAMENTO.map((nivel) => (
              <option key={nivel} value={nivel}>
                {ROTULO_ENGAJAMENTO[nivel]}
              </option>
            ))}
          </CampoSelecao>

          {/*
            As observações são do Lead e abrem preenchidas na edição. Antes o
            campo sumia aqui, e quem abria a venda para trabalhar nela não tinha
            onde colar o que ficou sabendo — nem via o que já tinha escrito no
            cadastro.

            Três casos na edição: com Lead, o campo abre com o que está
            guardado; sem Lead (venda da carteira importada), ele não aparece,
            porque não há onde guardar; e se a leitura falhou, ele também não
            aparece — mas com um aviso, e sem ser enviado, para o Salvar não
            apagar o que existe.
          */}
          {!editando ? (
            <CampoTextoLongo
              rotulo="Observações"
              name="observacoes"
              rows={5}
              className="md:col-span-2"
              dica={DICA_DAS_OBSERVACOES}
            />
          ) : observacoesDaVenda.data?.temLead ? (
            <CampoTextoLongo
              rotulo="Observações"
              name="observacoes"
              rows={5}
              className="md:col-span-2"
              defaultValue={observacoesDaVenda.data.observacoes ?? ''}
              dica={DICA_DAS_OBSERVACOES}
            />
          ) : observacoesDaVenda.isError ? (
            <p
              role="status"
              data-teste="observacoes-indisponiveis"
              className="rounded-[9px] bg-dourado-suave px-3 py-2 text-[12px] text-texto-suave md:col-span-2"
            >
              Não foi possível carregar as observações. Feche e abra a ficha de novo; o resto
              pode ser salvo sem mexer nelas.
            </p>
          ) : null}
        </div>

        {erro ? (
          <p role="alert" className="mt-4 rounded-lg bg-erro/10 px-3 py-2 text-sm text-erro">
            {erro}
          </p>
        ) : null}

        {duplicidades && duplicidades.length > 0 ? (
          <div
            data-teste="alerta-duplicidade"
            className="mt-4 rounded-[11px] border border-alerta/40 bg-dourado-suave p-4"
          >
            <p className="text-base font-[850] text-marinho">
              {editando
                ? 'Estes dados já pertencem a outro Contato'
                : 'Já existe um Contato com estes dados'}
            </p>
            <p className="mt-1 text-[12px] text-texto-suave">
              {editando
                ? 'Confira se não é a mesma pessoa cadastrada duas vezes. Se for outra, confirme abaixo e a correção é gravada.'
                : 'Reutilize a identidade para dar a este cliente mais uma venda sem duplicá-lo, ou confirme que se trata de outra pessoa ou empresa.'}
            </p>

            <ul className="mt-3 space-y-2">
              {duplicidades.map((duplicidade) => {
                // Numa const, e não inline: o estreitamento de `string | null`
                // não atravessa o JSX condicional do botão.
                const contatoId = duplicidade.contatoId;
                return (
                <li
                  key={duplicidade.contatoId ?? duplicidade.campos.join('-')}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-[9px] bg-superficie px-3 py-2.5"
                >
                  <div>
                    <p className="font-[750]">
                      {duplicidade.nome ?? 'Contato de outra responsável'}
                    </p>
                    <p className="text-[12px] text-texto-suave">
                      Coincide em: {duplicidade.campos.join(', ')}
                      {duplicidade.nome === null
                        ? ' · fale com a Administradora para reaproveitar esta identidade'
                        : ''}
                    </p>
                  </div>
                  {/* Reutilizar a identidade só faz sentido no cadastro: na
                    * edição não há venda nova para pendurar noutro Contato.
                    * E só com o id em mãos: sem ele, o Contato é de outra
                    * pessoa e quem decide reaproveitar é a Administradora. */}
                  {editando || contatoId === null ? null : (
                    <button
                      type="button"
                      onClick={() => associarAoContato(contatoId)}
                      className="rounded-[9px] bg-marinho px-3 py-1.5 text-[13px] font-[750] text-white transition hover:bg-marinho-claro"
                    >
                      Associar a este Contato
                    </button>
                  )}
                </li>
                );
              })}
            </ul>

            <button
              type="button"
              onClick={registrarComoOutraIdentidade}
              className="mt-3 rounded-[9px] border border-linha bg-superficie px-3 py-1.5 text-[13px] transition hover:bg-marinho-suave"
            >
              É outra pessoa ou empresa
            </button>
          </div>
        ) : null}

        {excluindo && oportunidade ? (
          <div className="mt-4">
            <ConfirmarExclusao
              rota={`/oportunidades/${oportunidade.id}`}
              aoVoltar={() => setExcluindo(false)}
              aoExcluir={aoFechar}
            />
          </div>
        ) : null}

        <div className="mt-4 flex justify-end gap-2">
          {/* Só a Administradora exclui, e só o que já existe. */}
          {editando && ehAdministradora && !excluindo ? (
            <button
              type="button"
              onClick={() => setExcluindo(true)}
              className="mr-auto rounded-[10px] border border-erro/40 bg-superficie px-3.25 py-2.5 text-base font-[750] text-erro transition hover:bg-erro/5"
            >
              Excluir
            </button>
          ) : null}
          <button
            type="button"
            onClick={aoFechar}
            className="rounded-[10px] border border-linha bg-superficie px-3.25 py-2.5 text-base font-[750] transition hover:bg-marinho-suave"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={salvar.isPending}
            className="rounded-[10px] bg-marinho px-3.25 py-2.5 text-base font-[750] text-white transition hover:bg-marinho-claro disabled:opacity-60"
          >
            Salvar
          </button>
        </div>
      </form>
    </Dialogo>
  );
}

/**
 * Os três tipos, mais o antigo quando é ele que está gravado nesta venda.
 *
 * Sem essa exceção, abrir uma venda anterior à separação encontraria um
 * `defaultValue` que não existe na lista: o navegador escolheria a primeira
 * opção e salvar o telefone trocaria o tipo de contrato sem ninguém pedir.
 */
function tiposDisponiveis(atual: string | undefined): TipoContrato[] {
  const novos: TipoContrato[] = [...TIPOS_CONTRATO];
  const ehLegado = Boolean(atual) && !novos.some((tipo) => tipo === atual);

  return ehLegado && atual! in ROTULO_TIPO_CONTRATO ? [...novos, atual as TipoContrato] : novos;
}
